import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { apiRequest } from '../../api/client';
import ReactMarkdown from 'react-markdown';

interface JobTags {
  language?: string[];
  specialization?: string[];
  industry?: string[];
  infrastructure?: string[];
  companyStage?: string[];
  experienceLevel?: string[];
}

interface Job {
  id: string;
  title: string;
  description: string;
  company: { id: string; name: string; slug: string };
  location: string;
  locationType: string;
  approved: boolean;
  sourceType: string;
  sourceUrl: string;
  createdAt: string;
  tags?: JobTags;
}

const TAG_COLORS: Record<string, string> = {
  language: 'bg-blue-500/20 text-blue-300',
  specialization: 'bg-purple-500/20 text-purple-300',
  industry: 'bg-green-500/20 text-green-300',
  infrastructure: 'bg-orange-500/20 text-orange-300',
  companyStage: 'bg-pink-500/20 text-pink-300',
  experienceLevel: 'bg-yellow-500/20 text-yellow-300',
};

const getDisplayTags = (tags?: JobTags): Array<{ category: string; value: string }> => {
  if (!tags) return [];
  const displayTags: Array<{ category: string; value: string }> = [];
  const categories: (keyof JobTags)[] = ['language', 'specialization', 'industry', 'infrastructure', 'companyStage', 'experienceLevel'];

  for (const category of categories) {
    const values = tags[category];
    if (values && values.length > 0) {
      displayTags.push({ category, value: values[0] });
    }
  }
  return displayTags;
};

const AdminJobs: React.FC = () => {
  const { getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  const approvedFilter = searchParams.get('approved');

  const toggleDescription = (jobId: string) => {
    setExpandedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(approvedFilter && { approved: approvedFilter }),
        ...(search && { search }),
      });

      const data = await apiRequest<{ jobs: Job[]; pagination: { pages: number; total: number } }>(
        `/admin/jobs?${params}`,
        {},
        token
      );
      setJobs(data.jobs);
      setTotalPages(data.pagination.pages);
      setTotalJobs(data.pagination.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, approvedFilter, page, search]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const publishJob = async (jobId: string) => {
    try {
      const token = await getToken();
      await apiRequest(`/admin/jobs/${jobId}/publish`, { method: 'POST' }, token);
      // Update local state instead of refetching
      setJobs(prev => prev.map(job =>
        job.id === jobId ? { ...job, approved: true } : job
      ));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const unpublishJob = async (jobId: string) => {
    try {
      const token = await getToken();
      await apiRequest(`/admin/jobs/${jobId}/unpublish`, { method: 'POST' }, token);
      // Update local state instead of refetching
      setJobs(prev => prev.map(job =>
        job.id === jobId ? { ...job, approved: false } : job
      ));
    } catch (err: any) {
      alert(err.message);
    }
  };


  const toggleSelectJob = (jobId: string) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedJobs.size === jobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(jobs.map(j => j.id)));
    }
  };

  const deleteSelected = async () => {
    if (selectedJobs.size === 0) return;

    if (!window.confirm(`Delete ${selectedJobs.size} selected job(s)?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const token = await getToken();
      await apiRequest('/admin/jobs/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selectedJobs) }),
      }, token);

      // Update local state instead of refetching
      setJobs(prev => prev.filter(job => !selectedJobs.has(job.id)));
      setSelectedJobs(new Set());
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const publishSelected = async () => {
    if (selectedJobs.size === 0) return;

    try {
      setIsPublishing(true);
      const token = await getToken();

      // Publish each selected job
      const jobIds = Array.from(selectedJobs);
      for (const jobId of jobIds) {
        await apiRequest(`/admin/jobs/${jobId}/publish`, { method: 'POST' }, token);
      }

      // Update local state
      setJobs(prev => prev.map(job =>
        selectedJobs.has(job.id) ? { ...job, approved: true } : job
      ));
      setSelectedJobs(new Set());
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const limit = 20;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalJobs);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-amber-400">Jobs Management</h1>
        <span className="text-cream-200/70">
          {totalJobs > 0 ? `Showing ${startItem}-${endItem} of ${totalJobs} jobs` : '0 jobs'}
        </span>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex gap-4">
          <select
            value={approvedFilter || ''}
            onChange={(e) => {
              if (e.target.value) {
                setSearchParams({ approved: e.target.value });
              } else {
                setSearchParams({});
              }
              setPage(1);
            }}
            className="bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
          >
            <option value="">All Jobs</option>
            <option value="true">Published</option>
            <option value="false">Unpublished</option>
          </select>
          <input
            type="text"
            placeholder="Search jobs..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600 flex-1 max-w-xs"
          />
        </div>

        {!loading && jobs.length > 0 && (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedJobs.size === jobs.length && jobs.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-oak-600 bg-oak-700 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-cream-200 text-sm">
                {selectedJobs.size === jobs.length && jobs.length > 0 ? 'Deselect All' : 'Select All'}
              </span>
            </label>

            {selectedJobs.size > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-cream-200/70 text-sm">
                  {selectedJobs.size} selected
                </span>
                <button
                  onClick={publishSelected}
                  disabled={isPublishing}
                  className="px-4 py-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isPublishing ? 'Publishing...' : 'Publish Selected'}
                </button>
                <button
                  onClick={deleteSelected}
                  disabled={isDeleting}
                  className="px-4 py-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-400 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-cream-200/50">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="text-cream-200/50">No jobs found</div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <div key={job.id} className="card-wood rounded-lg p-4 flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedJobs.has(job.id)}
                onChange={() => toggleSelectJob(job.id)}
                className="w-4 h-4 rounded border-oak-600 bg-oak-700 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {job.sourceUrl ? (
                    <a
                      href={job.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-amber-400 hover:underline"
                    >
                      {job.title}
                    </a>
                  ) : (
                    <span className="font-semibold text-cream-200">{job.title}</span>
                  )}
                  {job.approved ? (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex-shrink-0">
                      Published
                    </span>
                  ) : (
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded flex-shrink-0">
                      Draft
                    </span>
                  )}
                  {/* Tags - inline after status badge */}
                  {job.tags && getDisplayTags(job.tags).length > 0 && (
                    getDisplayTags(job.tags).map(({ category, value }) => (
                      <span
                        key={`${category}-${value}`}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${TAG_COLORS[category]}`}
                      >
                        {value}
                      </span>
                    ))
                  )}
                </div>
                <div className="text-sm text-cream-200/70">
                  {job.company.name} • {job.location}
                </div>
                <div className="text-xs text-cream-200/50 mt-1">
                  {job.sourceType} • {new Date(job.createdAt).toLocaleDateString()}
                </div>

                {/* Description */}
                {job.description && expandedJobs.has(job.id) && (
                  <div className="mt-3 pt-3 border-t border-oak-600">
                    <h4 className="text-sm font-semibold text-cream-200 mb-2">Job Description</h4>
                    <div className="text-sm text-cream-200/90 max-h-96 overflow-y-auto bg-oak-800 border border-oak-600 rounded-lg p-4 prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          h1: ({node, ...props}) => <h1 className="text-lg font-bold mt-4 mb-2 text-amber-400" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-base font-bold mt-3 mb-2 text-amber-400" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-sm font-semibold mt-2 mb-1 text-amber-400" {...props} />,
                          p: ({node, ...props}) => <p className="mb-2 text-cream-200/90" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2 space-y-1 text-cream-200/90" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2 space-y-1 text-cream-200/90" {...props} />,
                          li: ({node, ...props}) => <li className="ml-2" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-semibold text-cream-100" {...props} />,
                          em: ({node, ...props}) => <em className="italic" {...props} />,
                          a: ({node, ...props}) => <a className="text-amber-400 hover:underline" {...props} />,
                          code: ({node, ...props}) => <code className="bg-oak-900 px-1 py-0.5 rounded text-xs font-mono text-amber-300" {...props} />,
                          pre: ({node, ...props}) => <pre className="bg-oak-900 p-2 rounded overflow-x-auto text-xs" {...props} />,
                          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-amber-500 pl-4 italic my-2 text-cream-200/80" {...props} />,
                        }}
                      >
                        {job.description}
                      </ReactMarkdown>
                    </div>
                    <button
                      onClick={() => toggleDescription(job.id)}
                      className="text-amber-400 hover:underline text-xs mt-2"
                    >
                      Hide description
                    </button>
                  </div>
                )}
                {job.description && !expandedJobs.has(job.id) && (
                  <button
                    onClick={() => toggleDescription(job.id)}
                    className="text-amber-400 hover:underline text-xs mt-2"
                  >
                    View description
                  </button>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {job.approved ? (
                  <button
                    onClick={() => unpublishJob(job.id)}
                    className="text-xs bg-amber-500/20 text-amber-400 px-3 py-1 rounded hover:bg-amber-500/30"
                  >
                    Unpublish
                  </button>
                ) : (
                  <button
                    onClick={() => publishJob(job.id)}
                    className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded hover:bg-green-500/30"
                  >
                    Publish
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-oak-700 text-cream-200 rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-3 py-1 text-cream-200/70">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 bg-oak-700 text-cream-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminJobs;
